import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

import { useToast } from "../context/toastContext";
import SuccessModal from "../components/modal/SuccessModal";
import { authenticatedAxios } from "../apis";
import useEventStore from "../stores/events/event";

const InputGroup = ({ label, children, hint, required }) => (
  <div className="mb-5">
    <label className="text-left block text-sm font-semibold text-gray-800 mb-2">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
    {hint && <p className="text-left text-xs text-gray-500 mt-1.5">{hint}</p>}
  </div>
);

const Card = ({ title, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
    {title && (
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <h2 className="text-left text-lg font-bold text-gray-900">{title}</h2>
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

export default function EventEditManagement() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const textareaRef = useRef(null);
  const imageCounterRef = useRef(1);

  const eventDetail = useEventStore((s) => s.eventDetail);
  const markdownFromStore = useEventStore((s) => s.markdown);
  const loadingDetail = useEventStore((s) => s.loadingDetail);
  const loadingMarkdown = useEventStore((s) => s.loadingMarkdown);
  const loadingAction = useEventStore((s) => s.loadingAction);

  const fetchDetail = useEventStore((s) => s.actions.fetchDetail);
  const fetchMarkdown = useEventStore((s) => s.actions.fetchMarkdown);
  const updateEvent = useEventStore((s) => s.actions.updateEvent);

  const [showSuccess, setShowSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [tempImages, setTempImages] = useState({});

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [isPublished, setIsPublished] = useState(false);

  const [popupEnabled, setPopupEnabled] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [popupText, setPopupText] = useState("");
  const [popupImage, setPopupImage] = useState(null);
  const [popupImageUrl, setPopupImageUrl] = useState("");
  const [popupCtaText, setPopupCtaText] = useState("");
  const [popupCtaUrl, setPopupCtaUrl] = useState("");
  const [popupStartAt, setPopupStartAt] = useState("");
  const [popupEndAt, setPopupEndAt] = useState("");

  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [timezoneStr, setTimezoneStr] = useState("Asia/Jakarta");

  const [eventType, setEventType] = useState("ONLINE");
  const [platform, setPlatform] = useState("");
  const [meetingUrl, setMeetingUrl] = useState("");

  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState("");

  const [isFree, setIsFree] = useState(true);
  const [currency, setCurrency] = useState("USD");
  const [ticketPrice, setTicketPrice] = useState("");
  const [ticketNote, setTicketNote] = useState("");

  const [registerText, setRegisterText] = useState("Register");
  const [registerUrl, setRegisterUrl] = useState("");

  const [cardImage, setCardImage] = useState(null);
  const [heroImage, setHeroImage] = useState(null);
  const [cardImageUrl, setCardImageUrl] = useState("");
  const [heroImageUrl, setHeroImageUrl] = useState("");

  const [speakers, setSpeakers] = useState([
    { name: "", role: "", tagline: "", photo: "" },
  ]);

  const [speakerUploading, setSpeakerUploading] = useState({});
  const [speakerPickedName, setSpeakerPickedName] = useState({});

  const [whatYouGet, setWhatYouGet] = useState([""]);

  const addWhatYouGet = () => setWhatYouGet((prev) => [...prev, ""]);
  const removeWhatYouGet = (idx) => setWhatYouGet((prev) => prev.filter((_, i) => i !== idx));
  const updateWhatYouGet = (idx, value) =>
    setWhatYouGet((prev) => prev.map((v, i) => (i === idx ? value : v)));

  const normalizeWhatYouGet = (list) =>
    (list || []).map((x) => (x || "").trim()).filter(Boolean);

  const [localMarkdown, setLocalMarkdown] = useState("");

  useEffect(() => {
    fetchDetail(slug);
    fetchMarkdown(slug);
  }, [slug, fetchDetail, fetchMarkdown]);

  useEffect(() => {
    if (isFree) setTicketPrice("");
  }, [isFree]);

  useEffect(() => {
    if (!eventDetail) return;

    setTitle(eventDetail.title || "");
    setExcerpt(eventDetail.excerpt || "");
    setIsPublished(!!eventDetail.is_published);

    setPopupEnabled(!!eventDetail.popup_enabled);
    setPopupTitle(eventDetail.popup_title || "");
    setPopupText(eventDetail.popup_text || "");
    setPopupImageUrl(eventDetail.popup_image || "");
    setPopupCtaText(eventDetail.popup_cta_text || "");
    setPopupCtaUrl(eventDetail.popup_cta_url || "");
    setPopupStartAt(eventDetail.popup_start_at ? eventDetail.popup_start_at.slice(0, 16) : "");
    setPopupEndAt(eventDetail.popup_end_at ? eventDetail.popup_end_at.slice(0, 16) : "");

    setStartAt(eventDetail.start_at ? eventDetail.start_at.slice(0, 16) : "");
    setEndAt(eventDetail.end_at ? eventDetail.end_at.slice(0, 16) : "");
    setTimezoneStr(eventDetail.timezone_str || "Asia/Jakarta");

    setEventType(eventDetail.event_type || "ONLINE");
    setPlatform(eventDetail.platform || "");
    setMeetingUrl(eventDetail.meeting_url || "");
    setLocationName(eventDetail.location_name || "");
    setLocationAddress(eventDetail.location_address || "");

    setIsFree(eventDetail.is_free ?? true);
    setCurrency(eventDetail.currency || "USD");
    setTicketPrice(eventDetail.ticket_price || "");
    setTicketNote(eventDetail.ticket_note || "");

    setSpeakers(Array.isArray(eventDetail.speakers) ? eventDetail.speakers : []);

    setWhatYouGet(
      Array.isArray(eventDetail.what_you_get) && eventDetail.what_you_get.length
        ? eventDetail.what_you_get
        : [""]
    );

    setRegisterText(eventDetail.register_text || "Register");
    setRegisterUrl(eventDetail.register_url || "");

    setCardImageUrl(eventDetail.card_image || "");
    setHeroImageUrl(eventDetail.hero_image || "");
  }, [eventDetail]);

  useEffect(() => {
    setLocalMarkdown(markdownFromStore || "");
  }, [markdownFromStore]);

  const addSpeaker = () =>
    setSpeakers((prev) => [...prev, { name: "", role: "", tagline: "", photo: "" }]);

  const removeSpeaker = (idx) =>
    setSpeakers((prev) => prev.filter((_, i) => i !== idx));

  const updateSpeaker = (idx, key, value) =>
    setSpeakers((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s))
    );

  
  const generateSlug = (t) =>
    t
      .trim()
      .toLowerCase()
      .replace(/[^\w\u3131-\uD79D]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const normalizeSpeakers = (list) =>
    (list || [])
      .map((s) => ({
        name: (s.name || "").trim(),
        role: (s.role || "").trim(),
        tagline: (s.tagline || "").trim(),
        photo: (s.photo || "").trim(),
      }))
      .filter((s) => s.name);

  const insertAtCursor = (text) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const before = localMarkdown.substring(0, start);
    const after = localMarkdown.substring(end);

    const newValue = before + text + after;
    setLocalMarkdown(newValue);

    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
    }, 0);
  };

  const uploadSpeakerPhoto = async (idx, file) => {
    const MAX_SIZE = 3 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      addToast("error", "Speaker photo too large. Max 3MB.");
      return;
    }

    const fdImg = new FormData();
    fdImg.append("event_image", file);
    fdImg.append("slug", slug || generateSlug(title) || "temp");

    const token = localStorage.getItem("adminAccessToken");
    console.log("token", token);

    const { data } = await authenticatedAxios.post(
      "/api/admin/event/content/upload-image",
      fdImg,
      { headers: { "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
       } }
    );

    if (!data?.url) {
      addToast("error", "Failed uploading speaker photo.");
      return;
    }
    updateSpeaker(idx, "photo", data.url);
  };

  const handleMarkdownTempImage = async (file) => {
    const MAX_SIZE = 3 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      addToast("error", "Image is too large. Max 3MB allowed.");
      return;
    }
    const key = `image-${imageCounterRef.current++}`;
    const blobUrl = URL.createObjectURL(file);
    setTempImages((prev) => ({ ...prev, [key]: blobUrl }));
    insertAtCursor(`\n\n{{${key}}}\n\n`);
  };

  const handleSave = async () => {
    if (!title || !localMarkdown) {
      addToast("error", "Please fill required fields: title & content.");
      return;
    }

    setIsSaving(true);
    try {
      let finalMarkdown = localMarkdown;

      for (const [key, blobUrl] of Object.entries(tempImages)) {
        if (!finalMarkdown.includes(`{{${key}}}`)) continue;

        const response = await fetch(blobUrl);
        const blob = await response.blob();
        const file = new File([blob], `${key}.jpg`, { type: blob.type });

        const fdImg = new FormData();
        fdImg.append("event_image", file);
        fdImg.append("slug", slug);

        const token = localStorage.getItem("adminAccessToken");

        const { data } = await authenticatedAxios.post(
          "/api/admin/event/content/upload-image",
          fdImg,
          { headers: { "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
           } }
        );

        if (data?.url) {
          finalMarkdown = finalMarkdown.replace(
            new RegExp(`\\{\\{${key}\\}\\}`, "g"),
            `![image](${data.url})`
          );
        } else {
          addToast("error", `Failed uploading inline image ${key}`);
          setIsSaving(false);
          return;
        }
      }

      const mdBlob = new Blob([finalMarkdown], { type: "text/markdown" });
      const mdFile = new File([mdBlob], `${slug}.md`, { type: "text/markdown" });

      const fd = new FormData();
      fd.append("title", title);
      fd.append("excerpt", excerpt);
      fd.append("is_published", isPublished ? "true" : "false");
      fd.append("markdown", mdFile);

      fd.append("popup_enabled", popupEnabled ? "true" : "false");
      fd.append("popup_title", popupTitle);
      fd.append("popup_text", popupText);
      fd.append("popup_cta_text", popupCtaText);
      fd.append("popup_cta_url", popupCtaUrl);
      if (popupStartAt) fd.append("popup_start_at", popupStartAt);
      if (popupEndAt) fd.append("popup_end_at", popupEndAt);
      if (popupImage) fd.append("popup_image", popupImage);

      if (startAt) fd.append("start_at", startAt);
      if (endAt) fd.append("end_at", endAt);
      fd.append("timezone_str", timezoneStr);

      fd.append("event_type", eventType);
      fd.append("platform", platform);
      fd.append("meeting_url", meetingUrl);
      fd.append("location_name", locationName);
      fd.append("location_address", locationAddress);

      fd.append("is_free", isFree ? "true" : "false");
      fd.append("currency", currency);
      fd.append("ticket_price", ticketPrice);
      fd.append("ticket_note", ticketNote);

      fd.append("speakers_json", JSON.stringify(normalizeSpeakers(speakers)));

      fd.append("what_you_get_json", JSON.stringify(normalizeWhatYouGet(whatYouGet)));

      fd.append("register_text", registerText);
      fd.append("register_url", registerUrl);

      if (cardImage) fd.append("card_image", cardImage);
      if (heroImage) fd.append("hero_image", heroImage);

      const ok = await updateEvent(slug, fd);

      if (ok) {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          navigate("/admin/event-manage");
        }, 1200);
      } else {
        addToast("error", "Failed to update event.");
      }
    } catch (err) {
      console.error(err);
      addToast("error", "Unexpected error occurred.");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      Object.values(tempImages).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [tempImages]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      <SuccessModal
        show={showSuccess}
        message="Event updated successfully!"
        onClose={() => setShowSuccess(false)}
      />

      <div className="max-w-screen-2xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Event</h1>
              <p className="text-sm text-gray-600 mt-1 font-mono">{slug}</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => navigate(-1)}
                className="px-5 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50"
              >
                Back
              </button>

              <button
                onClick={handleSave}
                disabled={isSaving || loadingAction}
                className={`px-6 py-2.5 rounded-xl text-white font-semibold ${
                  isSaving || loadingAction ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>

        {(loadingDetail || loadingMarkdown) ? (
          <div className="text-gray-600 px-2">Loading event...</div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            <div className="xl:col-span-4 space-y-6">
              <Card title="Event Information">
                <InputGroup label="Title" required>
                  <input
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </InputGroup>

                <InputGroup label="Excerpt">
                  <textarea
                    rows={4}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 resize-none"
                    value={excerpt}
                    onChange={(e) => setExcerpt(e.target.value)}
                  />
                </InputGroup>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-800">
                    Published
                  </span>
                  <input
                    type="checkbox"
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                    className="w-5 h-5"
                  />
                </div>
              </Card>

              <Card title="Schedule">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputGroup label="Start At">
                    <input
                      type="datetime-local"
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                      value={startAt}
                      onChange={(e) => setStartAt(e.target.value)}
                    />
                  </InputGroup>

                  <InputGroup label="End At">
                    <input
                      type="datetime-local"
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                      value={endAt}
                      onChange={(e) => setEndAt(e.target.value)}
                    />
                  </InputGroup>
                </div>

                <InputGroup label="Timezone">
                  <input
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                    value={timezoneStr}
                    onChange={(e) => setTimezoneStr(e.target.value)}
                    placeholder="Asia/Jakarta"
                  />
                </InputGroup>
              </Card>

              <Card title="Venue">
                <InputGroup label="Event Type">
                  <select
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 bg-white"
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                  >
                    <option value="ONLINE">ONLINE</option>
                    <option value="OFFLINE">OFFLINE</option>
                  </select>
                </InputGroup>

                {eventType === "ONLINE" ? (
                  <>
                    <InputGroup label="Platform">
                      <input
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                        value={platform}
                        onChange={(e) => setPlatform(e.target.value)}
                        placeholder="Zoom"
                      />
                    </InputGroup>

                    <InputGroup label="Meeting URL">
                      <input
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                        value={meetingUrl}
                        onChange={(e) => setMeetingUrl(e.target.value)}
                        placeholder="https://..."
                      />
                    </InputGroup>
                  </>
                ) : (
                  <>
                    <InputGroup label="Location Name">
                      <input
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                        value={locationName}
                        onChange={(e) => setLocationName(e.target.value)}
                        placeholder="Venue name"
                      />
                    </InputGroup>

                    <InputGroup label="Address">
                      <textarea
                        rows={3}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 resize-none"
                        value={locationAddress}
                        onChange={(e) => setLocationAddress(e.target.value)}
                        placeholder="Full address..."
                      />
                    </InputGroup>
                  </>
                )}
              </Card>

              <Card title="Ticket & Register">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-gray-800">Free Event</span>
                  <input
                    type="checkbox"
                    checked={isFree}
                    onChange={(e) => setIsFree(e.target.checked)}
                    className="w-5 h-5"
                  />
                </div>

                {!isFree && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputGroup label="Currency">
                      <input
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        placeholder="USD"
                      />
                    </InputGroup>

                    <InputGroup label="Ticket Price">
                      <input
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                        value={ticketPrice}
                        onChange={(e) => setTicketPrice(e.target.value)}
                        placeholder="50"
                      />
                    </InputGroup>
                  </div>
                )}

                <InputGroup label="Ticket Note">
                  <input
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                    value={ticketNote}
                    onChange={(e) => setTicketNote(e.target.value)}
                    placeholder="Optional note near price"
                  />
                </InputGroup>

                <InputGroup label="Register Button Text">
                  <input
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                    value={registerText}
                    onChange={(e) => setRegisterText(e.target.value)}
                  />
                </InputGroup>

                <InputGroup label="Register URL">
                  <input
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                    value={registerUrl}
                    onChange={(e) => setRegisterUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </InputGroup>
              </Card>

              <Card title="Images">
                <InputGroup label="Card Image">
                  {cardImageUrl && !cardImage && (
                    <p className="text-xs text-gray-500 mb-2">
                      Current: <span className="font-mono break-all">{cardImageUrl}</span>
                    </p>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setCardImage(e.target.files?.[0] || null)}
                  />
                </InputGroup>

                <InputGroup label="Hero Image">
                  {heroImageUrl && !heroImage && (
                    <p className="text-xs text-gray-500 mb-2">
                      Current: <span className="font-mono break-all">{heroImageUrl}</span>
                    </p>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setHeroImage(e.target.files?.[0] || null)}
                  />
                </InputGroup>
              </Card>

              <Card title="Speakers">
                <div className="space-y-4">
                  {speakers.map((sp, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="font-semibold text-gray-900">Speaker #{idx + 1}</div>
                        {speakers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSpeaker(idx)}
                            className="text-sm px-3 py-1 rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputGroup label="Name" required>
                          <input
                            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                            value={sp.name}
                            onChange={(e) => updateSpeaker(idx, "name", e.target.value)}
                            placeholder="Speaker"
                          />
                        </InputGroup>

                        <InputGroup label="Role / Title">
                          <input
                            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                            value={sp.role}
                            onChange={(e) => updateSpeaker(idx, "role", e.target.value)}
                            placeholder="Marketing Manager"
                          />
                        </InputGroup>

                        <InputGroup label="Short Description" hint="One line, like your mockup">
                          <input
                            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3"
                            value={sp.tagline}
                            onChange={(e) => updateSpeaker(idx, "tagline", e.target.value)}
                            placeholder="Short Desc"
                          />
                        </InputGroup>

                        <InputGroup label="Photo">
                          {sp.photo && (
                            <div className="mb-2">
                              <img
                                src={sp.photo}
                                alt="speaker"
                                className="h-16 w-16 rounded-lg object-cover border"
                              />
                              <p className="text-xs text-gray-500 break-all mt-1">{sp.photo}</p>
                            </div>
                          )}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;

                                setSpeakerPickedName((p) => ({ ...p, [idx]: file.name }));
                                setSpeakerUploading((p) => ({ ...p, [idx]: true }));

                                try {
                                  await uploadSpeakerPhoto(idx, file);
                                } finally {
                                  setSpeakerUploading((p) => ({ ...p, [idx]: false }));
                                  e.target.value = "";
                                }
                              }}
                            />
                            {speakerPickedName[idx] && (
                              <p className="text-xs text-gray-500 mt-2">
                                Selected: <span className="font-mono">{speakerPickedName[idx]}</span>
                                {speakerUploading[idx] ? " (uploading...)" : ""}
                              </p>
                            )}

                        </InputGroup>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addSpeaker}
                    className="px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50"
                  >
                    + Add Speaker
                  </button>
                </div>
              </Card>

              <Card title="What will you get?">
                <div className="space-y-3">
                  {whatYouGet.map((item, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        className="flex-1 border-2 border-gray-200 rounded-xl px-4 py-3"
                        value={item}
                        onChange={(e) => updateWhatYouGet(idx, e.target.value)}
                        placeholder="e.g. Certificate, Free snacks, Recording access..."
                      />
                      <button
                        type="button"
                        onClick={() => removeWhatYouGet(idx)}
                        disabled={whatYouGet.length <= 1}
                        className="px-3 py-2 border-2 border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addWhatYouGet}
                    className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-gray-50"
                  >
                    + Add item
                  </button>
                </div>
              </Card>
            </div>

            <div className="xl:col-span-8 space-y-6">
              <Card title="Event Content (Markdown)">
                <div className="flex items-center gap-3 mb-4">
                  <label className="px-3 py-2 border rounded-lg cursor-pointer text-sm">
                    Insert Image
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleMarkdownTempImage(file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  <span className="text-xs text-gray-500">
                    Images upload to S3 on save.
                  </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <textarea
                    ref={textareaRef}
                    className="w-full min-h-[520px] border-2 border-gray-200 rounded-xl p-4 font-mono text-sm"
                    value={localMarkdown}
                    onChange={(e) => setLocalMarkdown(e.target.value)}
                    placeholder="Write event content..."
                  />

                  <div className="w-full min-h-[520px] border-2 border-gray-200 rounded-xl p-4 bg-gray-50 overflow-auto">
                    <ReactMarkdown
                      rehypePlugins={[
                        rehypeRaw,
                        [
                          rehypeSanitize,
                          {
                            ...defaultSchema,
                            attributes: {
                              ...defaultSchema.attributes,
                              img: ["src", "alt", "title"],
                            },
                          },
                        ],
                      ]}
                    >
                      {localMarkdown}
                    </ReactMarkdown>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
